import {
  ArrowLeft,
  Bot,
  CheckCircle2,
  CircleAlert,
  Clock3,
  ExternalLink,
  Gamepad2,
  Languages,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Tag,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, getAiProposals } from "./api";
import { LocaleToggle, useLocale, type DisplayLocale } from "./locale";
import type {
  AiFlashNewsEvent,
  AiProposalDecision,
  AiProposalFilter,
  AiProposalPreview,
  AiProposalSummary,
} from "./types";

const EMPTY_SUMMARY: AiProposalSummary = { publish: 0, discard: 0, review: 0 };

const FILTERS: Array<{
  id: AiProposalFilter;
  icon: typeof Sparkles;
}> = [
  { id: "all", icon: Sparkles },
  { id: "publish", icon: CheckCircle2 },
  { id: "discard", icon: Trash2 },
  { id: "review", icon: CircleAlert },
];

const FILTER_LABELS: Record<DisplayLocale, Record<AiProposalFilter, string>> = {
  en: { all: "All", publish: "Flash news", discard: "Discarded", review: "Review" },
  "zh-Hans": { all: "全部", publish: "生成快讯", discard: "已丢弃", review: "需复核" },
};

const DECISION_LABELS: Record<DisplayLocale, Record<AiProposalDecision, string>> = {
  en: { publish: "Flash news", discard: "Discarded", review: "Needs review" },
  "zh-Hans": { publish: "生成快讯", discard: "已丢弃", review: "需人工复核" },
};

const EVENT_TYPE_LABELS: Record<DisplayLocale, Record<string, string>> = {
  en: {
    system_update: "System update", game_update: "Game update", release: "Release",
    announcement: "Announcement", development: "Development", review: "Review",
    discount: "Discount", availability: "Availability", delay: "Delay",
    cancellation: "Cancellation", business: "Business", community: "Community",
    rumor: "Rumor", other: "Other",
  },
  "zh-Hans": {
    system_update: "系统更新", game_update: "游戏更新", release: "发售",
    announcement: "公告", development: "开发进展", review: "评测", discount: "折扣",
    availability: "上架动态", delay: "延期", cancellation: "取消", business: "商业动态",
    community: "社区", rumor: "传闻", other: "其他",
  },
};

const SOURCE_TYPE_LABELS: Record<DisplayLocale, Record<string, string>> = {
  en: {
    official: "Official", media: "Media", community: "Community", social: "Social media",
    store: "Store", aggregator: "Aggregator", other: "Other source",
  },
  "zh-Hans": {
    official: "官方", media: "媒体", community: "社区", social: "社交媒体",
    store: "商店", aggregator: "聚合来源", other: "其他来源",
  },
};

const REVIEW_COPY = {
  en: {
    missingTime: "Time unavailable", pageTitle: "AI flash news — GameNews", home: "GameNews home",
    lab: "AI LANGUAGE LAB", back: "Back to public news", eyebrow: "BILINGUAL FLASH PREVIEW",
    title: "AI flash news", hero: "Each Codex run produces one canonical English version and one Simplified Chinese version together.",
    shadowTitle: "Read-only shadow mode", shadowBody: "These results are previews and are not published to the public feed.",
    statsLabel: "AI processing summary", total: "Total", proposalUnit: "proposals", publish: "Flash news",
    discard: "Discarded", review: "Needs review", filtersLabel: "Filter AI decisions", syncing: "Syncing",
    refresh: "Refresh", loadError: "Unable to load AI results", errorTitle: "AI results are temporarily unavailable",
    retry: "Try again", noResults: "No AI results yet", noFilterResults: "No results for this filter",
    noResultsBody: "Results will appear after local Codex completes new shadow jobs.",
    noFilterResultsBody: "Choose another decision type to continue.", viewAll: "View all results",
    requestId: "Request ID", loading: "Loading AI results", atomicNews: "atomic flash items",
    confidence: "Overall confidence", originalSource: "SOURCE", official: "Official",
    sourceExplainerEn: "English is the canonical version generated in the same bilingual run.",
    sourceExplainerZh: "Simplified Chinese was generated alongside the canonical English version in the same Codex run.",
    sourceExplainerFallback: "The Chinese translation is not ready or did not pass validation, so the canonical English version is shown.",
    readOriginal: "Read source", canonicalPane: "AI ENGLISH CANONICAL", translatedPane: "AI SIMPLIFIED CHINESE VERSION",
    fallbackPane: "AI ENGLISH FALLBACK", translated: "Chinese version", fallback: "English fallback",
    translatedSummary: "Bilingual results ready", fallbackSummary: "English fallbacks",
    discardTitle: "No independent flash news item was extracted", discardFallback: "The model found no new game development to publish.",
    emptyEvents: "The model returned no flash news items", modelDecision: "Model decision",
    decisionFallback: "No additional decision reason was provided.", confidenceShort: "confidence", rumor: "Rumor",
    version: "Version", unscored: "No score", priceMissing: "Price unavailable", originalPrice: "Original",
    occurred: "Occurred", sourcePublished: "Source published", dealEnds: "Deal ends",
    candidateScore: "Candidate score", threshold: "AI threshold", selectedBecause: "Selected because",
    noSelectionSignals: "Passed the current editorial gate", scoringVersion: "Gate version",
  },
  "zh-Hans": {
    missingTime: "时间未提供", pageTitle: "AI 中英双语快讯 — GameNews", home: "GameNews 首页",
    lab: "AI 语言实验室", back: "返回公开新闻", eyebrow: "中英双语快讯预览",
    title: "AI 快讯语言预览", hero: "每次 Codex 加工都会同时生成一份英文事实母版和一份简体中文版本。",
    shadowTitle: "只读影子模式", shadowBody: "这里只展示加工结果，尚未进入公开信息流，也不会自动发布。",
    statsLabel: "AI 加工结果汇总", total: "结果总数", proposalUnit: "条 proposal", publish: "生成快讯",
    discard: "已丢弃", review: "需复核", filtersLabel: "筛选 AI 判断结果", syncing: "同步中",
    refresh: "刷新", loadError: "无法加载 AI 加工结果", errorTitle: "暂时无法读取 AI 结果",
    retry: "重新尝试", noResults: "还没有 AI 加工结果", noFilterResults: "这个筛选下没有结果",
    noResultsBody: "本机 Codex 完成新的影子任务后，结果会显示在这里。",
    noFilterResultsBody: "请选择其他判断类型继续查看。", viewAll: "查看全部结果",
    requestId: "本次读取编号", loading: "正在加载 AI 加工结果", atomicNews: "条原子快讯",
    confidence: "整体置信度", originalSource: "信息来源", official: "官方",
    sourceExplainerEn: "英文是同一次双语加工中生成的事实母版。",
    sourceExplainerZh: "简体中文与英文事实母版在同一次 Codex 加工中同步生成。",
    sourceExplainerFallback: "中文翻译尚未生成或未通过校验，因此右侧暂时显示英文事实母版。",
    readOriginal: "查看来源", canonicalPane: "AI 英文事实母版", translatedPane: "AI 简体中文版本",
    fallbackPane: "AI 英文回退", translated: "中文版本", fallback: "暂用英文",
    translatedSummary: "中英版本已同步生成", fallbackSummary: "暂用英文回退",
    discardTitle: "没有提取出值得发布的独立快讯", discardFallback: "模型判断该内容不构成新的游戏动态。",
    emptyEvents: "模型没有返回快讯条目", modelDecision: "模型判断",
    decisionFallback: "没有提供补充判断说明。", confidenceShort: "置信度", rumor: "传闻",
    version: "版本", unscored: "未给分", priceMissing: "价格未提供", originalPrice: "原价",
    occurred: "发生", sourcePublished: "来源发布", dealEnds: "优惠结束",
    candidateScore: "候选分", threshold: "AI 门槛", selectedBecause: "入选原因",
    noSelectionSignals: "通过当前编辑筛选门槛", scoringVersion: "筛选版本",
  },
} as const;

function formatTimestamp(timestamp: number, locale: DisplayLocale): string {
  if (!timestamp) return REVIEW_COPY[locale].missingTime;
  return new Intl.DateTimeFormat(locale === "zh-Hans" ? "zh-CN" : "en", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(timestamp);
}

function formatIsoTimestamp(timestamp: string | null, locale: DisplayLocale): string | null {
  if (!timestamp) return null;
  const value = Date.parse(timestamp);
  return Number.isFinite(value) ? formatTimestamp(value, locale) : timestamp;
}

function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function formatProposalConfidence(confidenceScoreBps: number | null): string {
  return confidenceScoreBps === null ? "—" : `${Math.round(confidenceScoreBps / 100)}%`;
}

function formatPrice(value: number | null, currency: string | null, locale: DisplayLocale): string | null {
  if (value === null) return null;
  const amount = new Intl.NumberFormat(locale === "zh-Hans" ? "zh-CN" : "en", { maximumFractionDigits: 2 }).format(value);
  return currency ? `${currency} ${amount}` : amount;
}

function proposalCount(filter: AiProposalFilter, total: number, summary: AiProposalSummary): number {
  return filter === "all" ? total : summary[filter];
}

const SELECTION_REASON_LABELS: Record<DisplayLocale, Record<string, string>> = {
  en: {
    "source:flagship": "Flagship source", "source:major_media": "Major media",
    "source:first_party": "First-party source", "source:trade_media": "Trade media",
    "source:specialist_media": "Specialist media", "source:regional_media": "Regional media",
    "event:acquisition_or_closure": "Acquisition / closure", "event:cancellation_or_delay": "Cancellation / delay",
    "event:hardware_reveal": "Hardware reveal", "event:release_date": "Release date",
    "event:system_update": "System update", "event:major_reveal": "Major reveal",
    "event:release": "Release", "event:review": "Review", "event:rumor": "Rumor",
    "event:deal": "Deal", "event:routine_patch": "Patch", "event:update": "Major update",
    "event:layoff_or_labor": "People / labor", "event:industry_report": "Industry report",
    "event:performance": "Performance", "event:personnel": "Personnel change",
    "event:generic_news": "Breaking development", "freshness:under_6h": "Under 6 hours old",
    "freshness:under_24h": "Under 24 hours old", "freshness:under_48h": "Under 48 hours old",
    "freshness:under_72h": "Under 72 hours old", "freshness:within_window": "Recent",
  },
  "zh-Hans": {
    "source:flagship": "头部来源", "source:major_media": "大型媒体",
    "source:first_party": "第一方来源", "source:trade_media": "行业媒体",
    "source:specialist_media": "专业媒体", "source:regional_media": "地区媒体",
    "event:acquisition_or_closure": "收购或关闭", "event:cancellation_or_delay": "取消或延期",
    "event:hardware_reveal": "硬件公布", "event:release_date": "发售日期",
    "event:system_update": "系统更新", "event:major_reveal": "重大公布",
    "event:release": "正式发售", "event:review": "评测", "event:rumor": "传闻",
    "event:deal": "折扣", "event:routine_patch": "补丁", "event:update": "重大更新",
    "event:layoff_or_labor": "人员或劳资动态", "event:industry_report": "行业报道",
    "event:performance": "性能表现", "event:personnel": "人事变动",
    "event:generic_news": "最新动态", "freshness:under_6h": "6 小时内",
    "freshness:under_24h": "24 小时内", "freshness:under_48h": "48 小时内",
    "freshness:under_72h": "72 小时内", "freshness:within_window": "近期内容",
  },
};

function humanizeSlug(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((part) => part.length <= 3 ? part.toUpperCase() : `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}

function selectionSignals(proposal: AiProposalPreview, locale: DisplayLocale): string[] {
  const entitySignals = proposal.selection.matchedEntities.map((entity) => humanizeSlug(entity));
  const ruleSignals = proposal.selection.reasonCodes
    .filter((reason) => reason.startsWith("source:") || reason.startsWith("event:") || reason.startsWith("freshness:"))
    .filter((reason) => reason !== "event:low_value_format")
    .map((reason) => SELECTION_REASON_LABELS[locale][reason] ?? "")
    .filter(Boolean);
  return [...new Set([...entitySignals, ...ruleSignals])].slice(0, 6);
}

function eventTaxonomy(event: AiFlashNewsEvent): string[] {
  return [...new Set([
    ...event.games,
    ...event.platforms,
    ...event.tags.map((tag) => tag.value),
  ])].slice(0, 10);
}

export default function AiReviewPage() {
  const { locale } = useLocale();
  const copy = REVIEW_COPY[locale];
  const filterLabels = FILTER_LABELS[locale];
  const [items, setItems] = useState<AiProposalPreview[]>([]);
  const [summary, setSummary] = useState<AiProposalSummary>(EMPTY_SUMMARY);
  const [totalCount, setTotalCount] = useState(0);
  const [requestId, setRequestId] = useState("");
  const [activeFilter, setActiveFilter] = useState<AiProposalFilter>("publish");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [translatedCount, setTranslatedCount] = useState(0);
  const [fallbackCount, setFallbackCount] = useState(0);
  const proposalRequestGeneration = useRef(0);

  const loadProposals = useCallback(async (signal?: AbortSignal) => {
    const generation = ++proposalRequestGeneration.current;
    setLoading(true);
    setError(null);
    setItems([]);
    setSummary(EMPTY_SUMMARY);
    setTotalCount(0);
    setRequestId("");
    setTranslatedCount(0);
    setFallbackCount(0);
    try {
      const response = await getAiProposals(locale, signal);
      if (generation !== proposalRequestGeneration.current) return;
      setItems(response.items);
      setSummary(response.summary);
      setTotalCount(response.count);
      setRequestId(response.requestId);
      setTranslatedCount(response.translatedCount);
      setFallbackCount(response.fallbackCount);
    } catch (requestError) {
      if (
        generation !== proposalRequestGeneration.current ||
        (requestError instanceof DOMException && requestError.name === "AbortError")
      ) return;
      setError(requestError instanceof Error ? requestError.message : copy.loadError);
    } finally {
      if (!signal?.aborted && generation === proposalRequestGeneration.current) {
        setLoading(false);
      }
    }
  }, [copy.loadError, locale]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = copy.pageTitle;
    const controller = new AbortController();
    void loadProposals(controller.signal);
    return () => {
      controller.abort();
      document.title = previousTitle;
    };
  }, [copy.pageTitle, loadProposals]);

  const visibleItems = useMemo(
    () => activeFilter === "all"
      ? items
      : items.filter((item) => item.decision === activeFilter),
    [activeFilter, items],
  );

  return (
    <div className="review-shell">
      <header className="topbar review-topbar">
        <a className="brand" href="/" aria-label={copy.home}>
          <span className="brand-mark"><Gamepad2 size={19} /></span>
          <span className="brand-word">GAME<span>//</span>NEWS</span>
        </a>
        <div className="review-topbar-label"><Bot size={15} /> {copy.lab}</div>
        <div className="review-top-actions">
          <LocaleToggle />
          <a className="review-back-link" href="/"><ArrowLeft size={15} /> {copy.back}</a>
        </div>
      </header>

      <main className="review-page">
        <section className="review-hero">
          <div>
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1>{copy.title}</h1>
            <p>{copy.hero}</p>
          </div>
          <div className="shadow-notice">
            <ShieldCheck size={18} />
            <span><strong>{copy.shadowTitle}</strong>{copy.shadowBody}</span>
          </div>
        </section>

        <section className="review-stats" aria-label={copy.statsLabel}>
          <div><span>{copy.total}</span><strong>{totalCount}</strong><small>{copy.proposalUnit}</small></div>
          <div className="stat-publish"><span>{copy.publish}</span><strong>{summary.publish}</strong><small>publish</small></div>
          <div className="stat-discard"><span>{copy.discard}</span><strong>{summary.discard}</strong><small>discard</small></div>
          <div className="stat-review"><span>{copy.review}</span><strong>{summary.review}</strong><small>review</small></div>
        </section>

        {locale === "zh-Hans" && !loading && !error && totalCount > 0 && (
          <div className="translation-summary" role="status">
            <span className="translated"><Languages size={13} /> {copy.translatedSummary} <strong>{translatedCount}</strong></span>
            <span className="fallback">{copy.fallbackSummary} <strong>{fallbackCount}</strong></span>
          </div>
        )}

        <div className="review-toolbar">
          <div className="review-filters" role="group" aria-label={copy.filtersLabel}>
            {FILTERS.map(({ id, icon: Icon }) => (
              <button
                key={id}
                className={activeFilter === id ? "active" : ""}
                onClick={() => setActiveFilter(id)}
                aria-pressed={activeFilter === id}
              >
                <Icon size={14} /> {filterLabels[id]}
                <span>{proposalCount(id, totalCount, summary)}</span>
              </button>
            ))}
          </div>
          <button className="review-refresh" onClick={() => { void loadProposals(); }} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spinning" : ""} />
            {loading ? copy.syncing : copy.refresh}
          </button>
        </div>

        {error && (
          <section className="error-card review-error">
            <strong>{copy.errorTitle}</strong>
            <p>{error}</p>
            <code>{API_BASE_URL}/api/v1/ai/proposals</code>
            <button onClick={() => { void loadProposals(); }}>{copy.retry}</button>
          </section>
        )}

        {!error && loading && items.length === 0 && <ReviewSkeleton label={copy.loading} />}

        {!error && !loading && visibleItems.length === 0 && (
          <section className="empty-state review-empty">
            <div><Bot size={25} /></div>
            <h2>{items.length === 0 ? copy.noResults : copy.noFilterResults}</h2>
            <p>{items.length === 0 ? copy.noResultsBody : copy.noFilterResultsBody}</p>
            {items.length > 0 && <button onClick={() => setActiveFilter("all")}>{copy.viewAll}</button>}
          </section>
        )}

        {!error && visibleItems.length > 0 && (
          <section className="proposal-list" aria-live="polite">
            {visibleItems.map((proposal) => <ProposalCard key={proposal.id} proposal={proposal} locale={locale} />)}
          </section>
        )}

        {!error && requestId && (
          <p className="review-request-id">{copy.requestId}: {requestId}</p>
        )}
      </main>
    </div>
  );
}

function ReviewSkeleton({ label }: { label: string }) {
  return (
    <div className="review-skeleton" aria-label={label}>
      {[0, 1].map((item) => (
        <div key={item}>
          <span />
          <section><i /><i /><i /><i /></section>
        </div>
      ))}
    </div>
  );
}

function ProposalCard({ proposal, locale }: { proposal: AiProposalPreview; locale: DisplayLocale }) {
  const copy = REVIEW_COPY[locale];
  const sourceType = SOURCE_TYPE_LABELS[locale][proposal.source.type] ?? proposal.source.type;
  const isTranslated = proposal.translationStatus === "translated";
  const isFallback = proposal.translationStatus === "fallback";
  const signals = selectionSignals(proposal, locale);
  return (
    <article className={`proposal-card proposal-${proposal.decision}`}>
      <header className="proposal-head">
        <div>
          <span className={`decision-badge decision-${proposal.decision}`}>
            {proposal.decision === "publish" && <CheckCircle2 size={13} />}
            {proposal.decision === "discard" && <Trash2 size={13} />}
            {proposal.decision === "review" && <CircleAlert size={13} />}
            {DECISION_LABELS[locale][proposal.decision]}
          </span>
          <span>{proposal.events.length} {copy.atomicNews}</span>
          {locale === "zh-Hans" && (
            <span className={`translation-badge ${isTranslated ? "translated" : "fallback"}`}>
              <Languages size={11} /> {isTranslated ? copy.translated : copy.fallback}
            </span>
          )}
        </div>
        <div>
          <span>{copy.candidateScore} <strong>{formatProposalConfidence(proposal.selection.scoreBps)}</strong></span>
          <span>{copy.confidence} <strong>{formatProposalConfidence(proposal.confidenceScoreBps)}</strong></span>
          <span><Clock3 size={12} /> {formatTimestamp(proposal.createdAt, locale)}</span>
        </div>
      </header>

      <div className="proposal-comparison">
        <section className="flash-pane">
          <div className="flash-pane-top">
            <p className="pane-kicker"><Bot size={13} /> {isTranslated ? copy.translatedPane : isFallback ? copy.fallbackPane : copy.canonicalPane}</p>
            <div className="source-attribution" aria-label={copy.originalSource}>
              <span className="source-avatar">{proposal.source.name.slice(0, 2).toUpperCase()}</span>
              <span className="source-name"><strong>{proposal.source.name}</strong><small>{sourceType}</small></span>
              {proposal.source.isOfficial && <span className="official-badge"><ShieldCheck size={11} /> {copy.official}</span>}
              <span className="source-time"><Clock3 size={12} /> {formatTimestamp(proposal.source.publishedAt, locale)}</span>
              {proposal.source.url && (
                <a className="source-open-link" href={proposal.source.url} target="_blank" rel="noreferrer">
                  {copy.readOriginal} <ExternalLink size={13} />
                </a>
              )}
            </div>
            <p className="source-explainer">
              {isTranslated ? copy.sourceExplainerZh : isFallback ? copy.sourceExplainerFallback : copy.sourceExplainerEn}
            </p>
            <div className="selection-explainer">
              <div>
                <span>{copy.selectedBecause}</span>
                <strong>{formatProposalConfidence(proposal.selection.scoreBps)}</strong>
                <small>{copy.threshold} {formatProposalConfidence(proposal.selection.thresholdBps)}</small>
              </div>
              <div className="selection-signals">
                {(signals.length > 0 ? signals : [copy.noSelectionSignals]).map((signal) => (
                  <span key={signal}>{signal}</span>
                ))}
              </div>
              <small>{copy.scoringVersion}: {proposal.selection.scoringVersion}</small>
            </div>
          </div>
          {proposal.decision === "discard" && proposal.events.length === 0 ? (
            <div className="discard-result">
              <Trash2 size={23} />
              <strong>{copy.discardTitle}</strong>
              <p>{proposal.decisionReason || copy.discardFallback}</p>
            </div>
          ) : (
            <div className="flash-event-list">
              {proposal.events.map((event, index) => (
                <FlashEventCard key={`${proposal.id}-${index}`} event={event} index={index} locale={locale} />
              ))}
              {proposal.events.length === 0 && (
                <div className="discard-result compact">
                  <CircleAlert size={20} />
                  <strong>{copy.emptyEvents}</strong>
                </div>
              )}
            </div>
          )}

          {!(proposal.decision === "discard" && proposal.events.length === 0) && (
            <div className="decision-reason">
              <span>{copy.modelDecision}</span>
              <p>{proposal.decisionReason || copy.decisionFallback}</p>
            </div>
          )}
        </section>
      </div>
    </article>
  );
}

function FlashEventCard({ event, index, locale }: { event: AiFlashNewsEvent; index: number; locale: DisplayLocale }) {
  const copy = REVIEW_COPY[locale];
  const occurredAt = formatIsoTimestamp(event.occurredAt, locale);
  const publishedAt = formatIsoTimestamp(event.publishedAt, locale);
  const labels = eventTaxonomy(event);
  const reviewScore = event.review?.score === null || event.review?.score === undefined
    ? null
    : event.review.maxScore === null
      ? String(event.review.score)
      : `${event.review.score} / ${event.review.maxScore}`;
  const originalPrice = event.discount ? formatPrice(event.discount.originalPrice, event.discount.currency, locale) : null;
  const salePrice = event.discount ? formatPrice(event.discount.salePrice, event.discount.currency, locale) : null;

  return (
    <article className="flash-event-card">
      <div className="flash-event-meta">
        <span className={`type-badge type-${event.eventType}`}>{EVENT_TYPE_LABELS[locale][event.eventType] ?? event.eventType}</span>
        <span>FLASH {String(index + 1).padStart(2, "0")}</span>
        <span className={`confidence-pill ${event.confidence < 0.7 ? "low" : ""}`}>{formatConfidence(event.confidence)} {copy.confidenceShort}</span>
        {event.isRumor && <span className="rumor-pill">{copy.rumor}</span>}
      </div>
      <h3>{event.title}</h3>
      {event.summary && <p className="flash-summary">{event.summary}</p>}

      {(event.version || event.review || event.discount) && (
        <div className="flash-facts">
          {event.version && <div><span>{copy.version}</span><strong>{event.version}</strong></div>}
          {event.review && (
            <div>
              <span>{event.review.publication}</span>
              <strong>{reviewScore ?? copy.unscored}</strong>
              {event.review.verdict && <small>{event.review.verdict}</small>}
            </div>
          )}
          {event.discount && (
            <div>
              <span>{event.discount.store}</span>
              <strong>{salePrice ?? copy.priceMissing}</strong>
              {originalPrice && <small>{copy.originalPrice} {originalPrice}</small>}
            </div>
          )}
        </div>
      )}

      {labels.length > 0 && (
        <div className="story-taxonomy flash-taxonomy">
          <Tag size={12} />
          {labels.map((label, labelIndex) => <span key={`${label}-${labelIndex}`}>{label}</span>)}
        </div>
      )}

      <div className="flash-dates">
        {occurredAt && <span>{copy.occurred}: {occurredAt}</span>}
        {publishedAt && <span>{copy.sourcePublished}: {publishedAt}</span>}
        {event.discount?.endsAt && <span>{copy.dealEnds}: {formatIsoTimestamp(event.discount.endsAt, locale)}</span>}
      </div>

    </article>
  );
}
